import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { camelCase, isEmpty } from 'lodash-es';
import { isElementOfType, Enum, loadGoogleApi, isDefined, safeInvoke } from '../../../../library';
import { Marker } from './marker';
import { Polygon } from './polygon';
var mapClasses = {
  MAP: 'map',
  GMAP: 'map__gmap'
};
var MapEventTypes = Enum({
  BOUNDS_CHANGED: 'bounds_changed',
  CENTER_CHANGED: 'center_changed',
  CLICK: 'click',
  DBLCLICK: 'dblclick',
  DRAG: 'drag',
  DRAG_END: 'dragend',
  DRAG_START: 'dragstart',
  HEADING_CHANGED: 'heading_changed',
  IDLE: 'idle',
  MAP_TYPE_ID_CHANGED: 'maptypeid_changed',
  MOUSE_MOVE: 'mousemove',
  MOUSE_OUT: 'mouseout',
  MOUSE_OVER: 'mouseover',
  PROJECTION_CHANGED: 'projection_changed',
  RIGHT_CLICK: 'rightclick',
  TILES_LOADED: 'tilesloaded',
  TILT_CHANGED: 'tilt_changed',
  ZOOM_CHANGED: 'zoom_changed'
});
export var MapRatioType = Enum({
  SQUARE: 'square',
  STANDARD: 'standard',
  WIDESCREEN: 'widescreen',
  UTRAWIDE: 'ultrawide'
});
export var MapContext = React.createContext({
  gApi: null,
  isLoaded: false,
  map: null
});
export var Map = function Map(props) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isLoaded = _useState2[0],
    setIsLoaded = _useState2[1];
  var mapRef = useRef(null);
  var listeners = useRef({});
  var gApi = useRef(null);
  var map = useRef(null);
  var classNames = cn(mapClasses.MAP, "-".concat(props.shape), _defineProperty({}, "-".concat(props.ratio), !props.height), props.className);
  var enableMapListeners = function enableMapListeners() {
    if (!isEmpty(listeners.current)) return;
    Object.values(MapEventTypes).forEach(function (eventType) {
      if (!map.current) return;
      listeners.current[eventType] = map.current.addListener(eventType, handleEvent(eventType));
    });
  };
  var removeMapListeners = function removeMapListeners() {
    Object.keys(listeners.current).forEach(function (e) {
      if (!gApi.current) return;
      gApi.current.maps.event.removeListener(listeners.current[e]);
      delete listeners.current[e];
    });
  };
  var disableMap = function disableMap() {
    var _map$current;
    (_map$current = map.current) === null || _map$current === void 0 || _map$current.setOptions({
      gestureHandling: 'none',
      zoomControl: false,
      clickableIcons: false,
      rotateControl: false
    });
    removeMapListeners();
  };
  var reEnableMap = function reEnableMap() {
    var _map$current2, _props$initOptions$ge, _props$initOptions, _props$initOptions$cl, _props$initOptions2, _props$initOptions$zo, _props$initOptions3, _props$initOptions$ro, _props$initOptions4;
    (_map$current2 = map.current) === null || _map$current2 === void 0 || _map$current2.setOptions({
      gestureHandling: (_props$initOptions$ge = (_props$initOptions = props.initOptions) === null || _props$initOptions === void 0 ? void 0 : _props$initOptions.gestureHandling) !== null && _props$initOptions$ge !== void 0 ? _props$initOptions$ge : 'cooperative',
      clickableIcons: (_props$initOptions$cl = (_props$initOptions2 = props.initOptions) === null || _props$initOptions2 === void 0 ? void 0 : _props$initOptions2.clickableIcons) !== null && _props$initOptions$cl !== void 0 ? _props$initOptions$cl : false,
      zoomControl: (_props$initOptions$zo = (_props$initOptions3 = props.initOptions) === null || _props$initOptions3 === void 0 ? void 0 : _props$initOptions3.zoomControl) !== null && _props$initOptions$zo !== void 0 ? _props$initOptions$zo : true,
      rotateControl: (_props$initOptions$ro = (_props$initOptions4 = props.initOptions) === null || _props$initOptions4 === void 0 ? void 0 : _props$initOptions4.rotateControl) !== null && _props$initOptions$ro !== void 0 ? _props$initOptions$ro : true
    });
    enableMapListeners();
  };
  var initMap = function initMap() {
    if (!gApi.current) return;
    var defaultOptions = {
      center: {
        lat: 0,
        lng: 0
      },
      clickableIcons: false,
      zoom: 6,
      mapTypeControl: false,
      mapTypeControlOptions: {},
      fullscreenControl: false,
      streetViewControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT
      },
      backgroundColor: 'transparent'
    };
    if (mapRef.current === null) return;
    map.current = new gApi.current.maps.Map(mapRef.current, _objectSpread(_objectSpread({}, defaultOptions), props.initOptions));
    setIsLoaded(true);
    if (props.isDisabled) {
      disableMap();
    } else {
      enableMapListeners();
    }
    safeInvoke(props.onReady, map.current);
  };
  var handleEvent = function handleEvent(evtName) {
    var timeout;
    var handlerName = camelCase("on_".concat(evtName));
    return function (e) {
      if (isDefined(timeout)) {
        clearTimeout(timeout);
        timeout = null;
      }
      timeout = window.setTimeout(function () {
        safeInvoke(props[handlerName], props, map.current, e);
      }, 0);
    };
  };
  useEffect(function () {
    loadGoogleApi(props.apiKey)["catch"](function (error) {
      console.error('Unable to load Google Api', error);
    }).then(function () {
      gApi.current = window.google;
      initMap();
    });
    return function () {
      removeMapListeners();
    };
  }, []);
  useEffect(function () {
    if (props.isDisabled) {
      disableMap();
    } else {
      reEnableMap();
    }
  }, [props.isDisabled]);
  var getValidChildren = function getValidChildren() {
    var children = props.children;
    if (!children) return null;
    return React.Children.map(children, function (child) {
      if (!React.isValidElement(child)) return undefined;
      if (isElementOfType(child, Marker) || isElementOfType(child, Polygon)) {
        return child;
      }
      return undefined;
    });
  };
  return React.createElement("div", {
    className: classNames,
    style: {
      height: props.height
    }
  }, React.createElement("div", {
    ref: mapRef,
    className: mapClasses.GMAP
  }, React.createElement(MapContext.Provider, {
    value: {
      gApi: gApi.current,
      isLoaded: isLoaded,
      map: map.current
    }
  }, isLoaded ? getValidChildren() : null)));
};
Map.displayName = 'Map';
Map.defaultProps = {
  ratio: MapRatioType.STANDARD,
  shape: 'rounded'
};
Map.Marker = Marker;
Map.Polygon = Polygon;